/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';
import { action, computed, get } from '@ember/object';
import { alias, filterBy, mapBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Options from '@linc-technologies/linced-common/enums/options';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import moment from 'moment';
import { scaleOrdinal } from 'd3-scale';
import {
  byKeys,
  filterBy as filterByArray,
  findBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

export default class IndexController extends Controller.extend(EditPostMixin) {
  @action
  setNewReportDate(value) {
    this.newReportDate = value;
  }

  queryParams = ['reportDate'];

  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  dates;

  @service
  features;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  terms;

  // Attrs
  reportIcon = 'address_book';

  @optionAlias(Options.REPORTING_REPORT_DESCRIPTION)
  reportDescription;
  @optionAlias(Options.REPORTING_REPORT_PAGE_NAME)
  reportPageName;
  @optionAlias(Options.ASSESSMENTS_SHOW_HOMEPAGE_OVERVIEW)
  showAssessmentOverview;
  @optionAlias(Options.REPORTING_REPORT_MAIN_BLOCKS)
  mainBlocks;
  @optionAlias(Options.REPORTING_REPORT_PAGE_BLOCKS)
  pageBlocks;
  @optionAlias(Options.REPORTING_REPORT_POSTS_PER_PAGE)
  numPostsPerPage;
  @optionAlias(Options.REPORTING_SHOW_SUBJECTS_ON)
  showSubjectsOn;
  @optionAlias(Options.REPORTING_SHOW_ACHIEVEMENT_KEY)
  showAchievementKey;
  @tracked currentDate = moment().format('YYYY-MM-DD');
  @tracked reportDate = moment().format('YYYY-MM-DD');
  @tracked
  newReportDate = '';
  @optionAlias(Options.REPORTING_MINI_GRADED_CARDS)
  gradedCards;
  @optionAlias('parents:reportPage:labelpages')
  labelPages;

  // State
  reportDetailsOpen = false;

  @tracked
  isMobile = true;
  @tracked isParentView = false;
  @tracked
  editingReportDate = false;
  @tracked timeWarpLoading = false;
  @tracked maxGradeColumns = 12;
  @tracked
  printDialogOpen = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  Jurisdiction = Jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  init() {
    super.init(...arguments);
    this.isMobile = isMobile();
  }

  // sortedTerms returns terms sorted by year and term limited to the
  // configured amount of terms to show.
  sortedTerms(gradeColumns) {
    let sortedTerms = this.termData.terms
      .slice()
      .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
    if (!isEmpty(this.termData.currentTerm)) {
      let index = sortedTerms.indexOf(this.termData.currentTerm);
      let minIndex = Math.max(0, index - gradeColumns + 1);
      return sortedTerms.slice(minIndex, index + 1);
    }

    return sortedTerms;
  }

  // configuredTimeframes gets timeframes limited to the
  // configured amount of columns to show.
  configuredTimeframes(gradeColumns) {
    let index = 0;
    let minIndex = 0;
    let sortedTimeframes = this.model.timeframes;
    let currentTimeframe = findBy(this.model.timeframes, 'isCurrent');
    let closestTimeframe = this.dates.findClosestTimeframe(
      this.model.timeframes,
    );
    if (!isEmpty(currentTimeframe)) {
      index = sortedTimeframes.indexOf(currentTimeframe);
      minIndex = Math.max(0, index - gradeColumns + 1);
    } else {
      // we're in between timeframes, return the closest one and beyond
      index = sortedTimeframes.indexOf(closestTimeframe);
      minIndex = Math.max(0, index - gradeColumns + 1);
    }
    return sortedTimeframes.slice(minIndex, index + 1);
  }

  getAlternateCutOffDate(gradeColumns) {
    // return term cut off date if no timeframes
    if (isEmpty(this.model.timeframes)) {
      return this.getTermCutOffDate(gradeColumns);
    }
    return this.configuredTimeframes(gradeColumns).reverse().at(-1)
      .startDateUnix;
  }

  monthCutOffDate(gradeColumns) {
    let today = moment();
    let finalMonth = moment(today)
      .subtract(gradeColumns, 'months')
      .startOf('month');
    return moment(finalMonth).endOf('month').unix();
  }

  @computed('terms.{currentAcademicYearTerms,currentTerm}')
  get termData() {
    return {
      terms: this.terms.currentAcademicYearTerms,
      currentTerm: this.terms.currentTerm,
    };
  }

  getTermCutOffDate(gradeColumns) {
    return moment(
      this.sortedTerms(gradeColumns).reverse().at(-1).start,
      'YYYY-MM-DD',
    ).unix();
  }

  // gradeAssessments provides an intermediary CP so the @each dependent key
  // below stays one level deep.
  @mapBy('grades', 'assessment')
  gradeAssessments;

  @computed(
    'grades.@each.createTime',
    'gradeAssessments.@each.{assessmentDisplay,noGradesColumnsToShowCaregiversStudents}',
  )
  get filteredGrades() {
    return this.grades.filter((grade) => {
      let gradeColumns = 4;
      if (grade.get('assessment.noGradesColumnsToShowCaregiversStudents')) {
        gradeColumns = grade.get(
          'assessment.noGradesColumnsToShowCaregiversStudents',
        );
      }
      switch (grade.get('assessment.assessmentDisplay')) {
        case AssessmentDisplay.ALTERNATE:
          return grade.createTime > this.getAlternateCutOffDate(gradeColumns);
        case AssessmentDisplay.MONTHS:
          return grade.createTime > this.monthCutOffDate(gradeColumns);
        default:
          return grade.createTime > this.getTermCutOffDate(gradeColumns);
      }
    });
  }

  @computed('showSubjectsOn')
  get showSubjects() {
    if (isEmpty(this.showSubjectsOn)) {
      // Show by default if the option isn't set yet.
      return true;
    }
    return ['report', 'both'].includes(this.showSubjectsOn);
  }

  @computed(
    'model.{goalSubjects.[],developingStates.[],secureStates.[]}',
    'isParentView',
  )
  get goalSubjects() {
    let ids = this.model.developingStates.map((item) => item.goal_subject_id);
    // Only include subjects based on secure states if goal states
    // are updated/secured after rollover date.
    if (!isEmpty(this.model.secureStates)) {
      ids.push(...this.model.secureStates.map((item) => item.goal_subject_id));
    }

    ids = uniq(ids);
    let visibilityFlag = 'visibleToTeachers';
    if (this.isParentView) {
      visibilityFlag = 'visibleToParents';
    }
    return this.model.goalSubjects.filter((subject) => {
      // Display this subject if it's trackable OR if it's visible to
      // teachers and has developing states, unless hidden from reports.
      return (
        subject.reportVisibility !== SubjectReportVisibility.NEVER &&
        (subject.trackable ||
          (subject[visibilityFlag] && ids.includes(subject.id)))
      );
    });
  }

  @computed('goalSubjects.[]', 'model.pages.@each.goalSubject_ids')
  get unassignedSubjects() {
    // Ensure `goalSubjects` and `model.pages` are defined
    const goalSubjects = this.goalSubjects || [];
    const pages = this.model.pages || [];

    // Collect all assigned subject IDs from the pages
    const assignedSubjectIds = pages.reduce((ids, page) => {
      return ids.concat(page.goalSubject_ids || []);
    }, []);

    // Return subjects not in the list of assigned IDs, which are set to show
    // on reports despite not being on a Page that's shown on reports.
    return goalSubjects.filter(
      (subject) =>
        subject &&
        !assignedSubjectIds.includes(subject.id) &&
        subject.reportVisibility === SubjectReportVisibility.ALWAYS,
    );
  }

  @computed('showAssessmentOverview', 'isParentView')
  get showAssessments() {
    return this.isParentView ? this.showAssessmentOverview : true;
  }

  @filterBy('model.pages', 'hasAssessments')
  assessmentPages;

  @computed('assessmentPages.[]', 'model.assessments.[]', 'isParentView')
  get assessments() {
    if (this.isParentView) {
      // Duplicate what happens in the route but with a subset of pages
      let parentAssessmentPages = this.assessmentPages.filter(
        (item) => item.visibleToParents,
      );
      return uniq(
        parentAssessmentPages.reduce((assessments, page) => {
          // All pages should have all their assessments loaded already
          assessments.push(...page.assessments);
          return assessments;
        }, []),
      );
    }
    return this.model.assessments;
  }

  // grades is a list of grades for assessments which should be shown,
  // filtered down to those which were awarded within the past four terms.
  @computed(
    'model.grades.@each.createTime',
    'terms.currentTerm',
    'maxGradeColumns',
    'reportDate',
  )
  get grades() {
    if (isEmpty(this.model.grades)) {
      return [];
    }
    let term = this.terms.currentTerm;
    if (!isEmpty(this.reportDate)) {
      term = this.terms.currentTermAtDate(this.reportDate);
    }
    if (isEmpty(term)) {
      return [];
    }
    let termCount = 1;
    while (termCount < this.maxGradeColumns) {
      if (!isEmpty(term.get('previous.term'))) {
        term = term.get('previous');
      } else {
        break;
      }
      termCount++;
    }
    let startTime = moment(term.get('start'), 'YYYY-MM-DD').unix();
    return this.model.grades.filter((grade) => {
      return grade.createTime >= startTime;
    });
  }

  // isStudent computes if the incoming person is of type learner.
  @computed('model.person.type')
  get isStudent() {
    return this.model.person.type === PersonType.LEARNER;
  }

  @computed('model.person.type')
  get pages() {
    let type = this.get('model.person.type');
    if (type) {
      return this.store
        .peekAll('posts/page')
        .filter((page) => page.type === type);
    } else {
      return [];
    }
  }

  @computed('reportDate')
  get currentYear() {
    return moment(this.reportDate).year();
  }

  @computed('currentYear', 'model.attendanceSummary.terms.@each.year')
  get attendanceTerms() {
    return filterByArray(
      this.get('model.attendanceSummary.terms') ?? [],
      'year',
      this.currentYear,
    ).sort(byKeys('term'));
  }

  @computed('model.posts.@each.{assessment_id,page_id,status}', 'isParentView')
  get linkedPosts() {
    let posts = this.get('model.posts');

    if (this.isParentView) {
      // Hide unpublished posts for caregivers
      posts = posts.filter((post) => {
        return post.status === PostStatus.PUBLISHED;
      });
    }

    // Default linked posts filtering
    posts = posts.filter((post) => {
      return (
        !isEmpty(post.get('assessment_id')) && !isEmpty(post.get('page_id'))
      );
    });

    // Group posts by page and assessment
    let groupedPosts = {};

    posts.forEach((post) => {
      let pageId = post.get('page_id');
      let assessmentId = post.get('assessment_id');

      if (!groupedPosts[pageId]) {
        groupedPosts[pageId] = {};
      }

      if (!groupedPosts[pageId][assessmentId]) {
        groupedPosts[pageId][assessmentId] = [];
      }

      groupedPosts[pageId][assessmentId].push(post);
    });

    return groupedPosts;
  }

  @computed('model.posts.@each.{assessment_id,status}', 'isParentView')
  get unlinkedPosts() {
    if (this.isParentView) {
      return this.model.posts.filter((post) => {
        return (
          post.status === PostStatus.PUBLISHED &&
          isEmpty(post.get('assessment_id'))
        );
      });
    }
    return this.model.posts.filter((item) => !item.assessment_id);
  }

  @computed('model.school')
  get periods() {
    if (this.trackMovements) {
      return 'Days';
    }
    return this.options.schoolGet(
      this.model.school,
      'core:attendance:periodsLabel',
    );
  }

  @optionAlias(Options.ATTENDANCE_SHOW_TO_CAREGIVERS)
  _showAttendance;

  @computed('isParentView', '_showAttendance')
  get showAttendance() {
    return this.isParentView ? this._showAttendance : true;
  }

  // defaultDate computes whether the report is showing the default date range
  // (i.e. all data for the current year).
  @computed('reportDate', 'currentDate')
  get defaultDate() {
    return isEmpty(this.reportDate) || this.reportDate === this.currentDate;
  }

  // reportTitle computes a dynamic report title based on the date range.
  @computed('defaultDate', 'reportDate', 'reportPageName')
  get reportTitle() {
    if (this.defaultDate) {
      return this.reportPageName || `${moment().year()} Report`;
    }
    return `Report at ${moment(this.reportDate).format('Do MMM YYYY')}`;
  }

  // endTime computes a timestamp from the report date, to be fed into any
  // components which fetch/filter their own data.
  get endTime() {
    if (this.defaultDate) {
      return null;
    }
    return moment(this.reportDate, 'YYYY-MM-DD').unix();
  }

  get numPlpGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:numCurrentGoals') || 5
      : undefined;
  }

  @optionAlias('parents:goals:showEvidence')
  showEvidence;

  get showPlpGoals() {
    if (!this.isParentView) {
      return true;
    }
    return (
      this.options.value('parents:goals:visible') &&
      this.options.value('parents:goals:showCurrent')
    );
  }

  @computed('model.attendanceRecords.[]')
  get hasMorHCode() {
    return !!(
      this.model.attendanceRecords.find((item) => item.code === 'H') ||
      this.model.attendanceRecords.find((item) => item.code === 'M')
    );
  }

  @computed('reportDate')
  get currentAcademicYearTerms() {
    let previousRollover = this.dates.previousRolloverDate(this.reportDate);
    let nextRollover = this.dates.nextRolloverDate(this.reportDate);
    return this.terms.getAcademicYearTermsForDate(
      previousRollover,
      nextRollover,
    );
  }

  @computed('model.dayCodes.[]', 'currentAcademicYearTerms')
  get byTruancyCode() {
    let data = [];
    let rawData = {};
    let halfDaysMap = [];
    let allDayCodes = this.model.dayCodes;
    let totalDayCodes = 0;
    let scheme = this.getColourScheme();
    // For each term this academic year, build up data to be charted.
    this.currentAcademicYearTerms.forEach((term) => {
      let currTermAttendance = {
        term: term.term,
        year: 0,
        present: 0,
        justified: 0,
        unjustified: 0,
        total: 0,
      };

      // Build up map of truancyCode -> count
      rawData = allDayCodes.reduce((data, dayCode) => {
        // Return early if dayCode is outside the current term
        if (dayCode.date < term.start || dayCode.date > term.end) {
          return data;
        }

        // Increment total dayCodes
        totalDayCodes++;

        let updateData = (key, value) => {
          data[key] = (data[key] || 0) + value;
        };

        if (dayCode.present > 0) {
          currTermAttendance.present += dayCode.presentWholeDays;
          updateData('P', dayCode.presentWholeDays);
        }
        if (dayCode.unjustifiablyAbsent > 0) {
          currTermAttendance.unjustified +=
            dayCode.unjustifiablyAbsentWholeDays;
          updateData('U', dayCode.unjustifiablyAbsentWholeDays);
        }
        if (dayCode.justifiedAbsent > 0) {
          currTermAttendance.justified += dayCode.justifiedAbsentWholeDays;
          updateData('J', dayCode.justifiedAbsentWholeDays);
        }

        return data;
      }, {});

      // Flip map into a ready-to-chart array and push it into the array of terms to return.
      let termData = this.mapData(rawData, totalDayCodes, 'truancy', scheme);
      data.push({
        term: term.term,
        data: termData,
      });
      currTermAttendance.total =
        currTermAttendance.justified +
        currTermAttendance.present +
        currTermAttendance.unjustified;
      // only add to the map if the term has data
      if (currTermAttendance.total > 0) {
        halfDaysMap.push(currTermAttendance);
      }
    });

    return { data, halfDaysMap };
  }

  mapData(rawData, total, type, scheme) {
    let data = [];
    total = total * 2;
    Object.keys(rawData).forEach((code) => {
      let label = this.getLabel(code, type);
      let count = rawData[code];

      // Handle pluralization for 'days'
      let displayMeasure = this.periods;
      if (displayMeasure === 'Days' && count === 1) {
        displayMeasure = 'Day';
      }

      data.push({
        value: code,
        count,
        tooltip: `${label} (${code}): ${count} ${displayMeasure}`,
        color: this.getColour(code, scheme),
      });
    });

    return data;
  }

  getColourScheme() {
    return scaleOrdinal([
      '#d62728',
      '#ff9896',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf',
      '#aec7e8',
      '#ffbb78',
      '#98df8a',
      '#c5b0d5',
      '#c49c94',
      '#f7b6d2',
      '#c7c7c7',
      '#dbdb8d',
      '#9edae5',
    ]);
  }

  getLabel(code, type) {
    switch (type) {
      case 'truancy':
        return this.attendanceCodes.getLabelForTruancyCode(code);
      case 'raw':
        return get(
          this.attendanceCodes.allCodes.find((item) => item.code === code),
          'label',
        );
    }

    return 'Unknown';
  }

  getColour(code, scheme) {
    switch (code) {
      case 'P':
        return '#2ca02c';
      case 'J':
        return '#ff7f0e';
      case 'U':
        return '#d62728';
    }

    // Fall back to colour scheme.
    return scheme(code);
  }

  @action
  refresh() {
    this.get('model.posts').update();
  }

  @action
  printPage() {
    this.printDialogOpen = true;
  }

  @action
  closePrintDialog() {
    this.printDialogOpen = false;
  }

  @action
  editReportDate() {
    if (this.editingReportDate) {
      this.editingReportDate = false;
    } else {
      this.newReportDate = this.reportDate;
      this.editingReportDate = true;
    }
  }

  @action
  updateDate() {
    this.timeWarpLoading = true;
    this.reportDate = this.newReportDate;
    this.editingReportDate = false;
  }

  @action
  resetDate() {
    this.timeWarpLoading = true;
    this.reportDate = moment().format('YYYY-MM-DD');
    this.editingReportDate = false;
  }

  reset() {
    this.isParentView = false;
    this.reportDate = moment().format('YYYY-MM-DD');
    this.editingReportDate = false;
    this.timeWarpLoading = false;
  }
}
